import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API_CONFIG, { getApiUrl } from '../../config/api';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Save as SaveIcon,
  QuestionAnswer as QuestionIcon,
} from '@mui/icons-material';

import { setClarityHost, sendClarityEvent, setClarityUser } from '../../utils/clarity';

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

const CreateWithAI = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const [topic, setTopic] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [surveyName, setSurveyName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [previousQuestions, setPreviousQuestions] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      try {
        // register user with Clarity on initial render of this screen
        setClarityUser(null, user?.uid || user?.id || null);
      } catch (e) {
        console.warn('Failed to set clarity user', e);
      }
    }
  }, [user, navigate]);

  const handleTopicChange = (e) => {
    setTopic(e.target.value);
  };

  const generateQuestions = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic for your survey');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      // Call the API to generate questions
      const response = await fetch(getApiUrl('/generate_survey_questions.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: topic,
          previous_questions: previousQuestions, // Send previously generated questions to avoid duplicates
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate questions');
      }

      // Update the generated questions
      setGeneratedQuestions(data.questions || []);

      // Clear selected questions when generating new ones
      setSelectedQuestions([]);

       try {
        if (user) setClarityHost(user);
        sendClarityEvent('ai_questions_generated', {
          topic,
          generatedCount: (data.questions || []).length,
          userId: user?.uid || null,
          timestamp: new Date().toISOString()
        });
      } catch (e) {
        console.warn('Clarity error while reporting AI generation', e);
      }

    } catch (err) {
      console.error('Error generating questions:', err);
      setError(err.message || 'Failed to generate questions. Please check your internet connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefresh = () => {
    // Store current questions to avoid duplicates
    setPreviousQuestions([...previousQuestions, ...generatedQuestions]);
    generateQuestions();
  };

  const handleQuestionSelect = (question) => {
    const isSelected = selectedQuestions.some(q => q.text === question.text);

    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q.text !== question.text));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === generatedQuestions.length) {
      // If all are selected, deselect all
      setSelectedQuestions([]);
    } else {
      // Otherwise, select all
      setSelectedQuestions([...generatedQuestions]);
    }
  };

  const handleCreateSurvey = () => {
    if (selectedQuestions.length === 0) {
      setError('Please select at least one question');
      return;
    }

    setNameModalOpen(true);
  };

  const handleSaveSurvey = async () => {
    if (!surveyName.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // First create the survey
      const createSurveyResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.CREATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          name: surveyName,
          theme: { name: 'default' } // Default theme
        })
      });

      const surveyData = await createSurveyResponse.json();

      if (!createSurveyResponse.ok) {
        throw new Error(surveyData.message || 'Failed to create survey');
      }

      const surveyId = surveyData.survey_id;

      // Then add the questions
      const questionsData = selectedQuestions.map((question, index) => ({
        survey_id: surveyId,
        question_type: question.type || 'short-answer', // Default to short-answer if not specified
        question_text: question.text,
        options: JSON.stringify(question.options || []),
        required: true,
        order_index: index
      }));

      const questionsResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.BULK_UPDATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questions: questionsData })
      });

      if (!questionsResponse.ok) {
        const errorData = await questionsResponse.json();
        throw new Error(errorData.message || 'Failed to add questions to survey');
      }

      // Navigate to the survey editor
      navigate(`/survey/edit/${surveyId}`);
    } catch (err) {
      console.error('Error saving survey:', err);
      setError(err.message || 'Failed to save survey. Please try again.');
    } finally {
      setIsSaving(false);
      setNameModalOpen(false);
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid',
          borderColor: 'grey.100'
        }}
      >
        <Toolbar sx={{ minHeight: '64px' }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ color: theme.palette.text.primary, mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={logo}
              alt="SurveySlack Logo"
              style={{
                height: '32px',
                marginRight: '12px'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '1.25rem',
                letterSpacing: '-0.025em'
              }}
            >
              Create Survey with AI
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 10, pb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Enter a topic for your survey
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Our AI will generate relevant questions based on your topic. You can then select which questions to include in your survey.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Survey Topic"
              variant="outlined"
              value={topic}
              onChange={handleTopicChange}
              placeholder="e.g., Customer Satisfaction, Employee Feedback, Product Evaluation"
              disabled={isGenerating}
            />
            <Button
              variant="contained"
              onClick={generateQuestions}
              disabled={!topic.trim() || isGenerating}
              sx={{ minWidth: '120px' }}
            >
              {isGenerating ? <CircularProgress size={24} /> : 'Generate'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {generatedQuestions.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              overflow: 'hidden'
            }}
          >
            <Box sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'grey.50'
            }}>
              <Typography variant="subtitle1" fontWeight={500}>
                Generated Questions
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ mr: 1 }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSelectAll}
                >
                  {selectedQuestions.length === generatedQuestions.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
            </Box>

            <List sx={{ p: 0 }}>
              {generatedQuestions.map((question, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      py: 2,
                      bgcolor: selectedQuestions.some(q => q.text === question.text)
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'transparent'
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedQuestions.some(q => q.text === question.text)}
                        onChange={() => handleQuestionSelect(question)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {question.text}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Type: {question.type || 'Short Answer'}
                        </Typography>
                      }
                    />
                    <IconButton
                      edge="end"
                      onClick={() => handleQuestionSelect(question)}
                      color={selectedQuestions.some(q => q.text === question.text) ? 'primary' : 'default'}
                    >
                      <AddIcon />
                    </IconButton>
                  </ListItem>
                  {index < generatedQuestions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleCreateSurvey}
                disabled={selectedQuestions.length === 0}
                fullWidth
              >
                Create Survey with Selected Questions ({selectedQuestions.length})
              </Button>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Add Name Dialog */}
      <Dialog
        open={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle>Name your survey</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Survey Name"
            type="text"
            fullWidth
            variant="outlined"
            value={surveyName}
            onChange={(e) => setSurveyName(e.target.value)}
            placeholder={topic ? `${topic} Survey` : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSurvey}
            variant="contained"
            disabled={!surveyName.trim() || isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Create Survey'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateWithAI;